import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, MapPin, Building2, Users, Truck, Printer, Trash2, ExternalLink, Package, ScanLine, CheckCircle2, Route as RouteIcon } from "lucide-react";
import { toast } from "sonner";
import RutaAdjuntos from "@/components/RutaAdjuntos";
import CargaRuta from "@/pages/CargaRuta";
import GastosBlock from "@/components/GastosBlock";
import RutaMapa from "@/components/RutaMapa";
import ResetParadaDialog from "@/components/ResetParadaDialog";
import RutaIntegridadDialog from "@/components/RutaIntegridadDialog";
import { DestinoHeader, DestinosResumen, destinoDeParada } from "@/components/DestinoHeader";
import DetalleMontaje from "@/components/DetalleMontaje";
import { useAuth } from "@/context/AuthContext";
import { RouteXLOptimizeDialog } from "@/components/RouteXLOptimizeDialog";
import ParteVehiculoBlock from "@/components/ParteVehiculoBlock";
import PlanificarParadaDialog from "@/components/PlanificarParadaDialog";
import EditMontadoresVehiculoDialog from "@/components/EditMontadoresVehiculoDialog";
import AddParadaExtraDialog from "@/components/AddParadaExtraDialog";
import { RutaEditPinDialog, AddLineasRutaDialog } from "@/components/RutaEditPin";
import { ViewInstruccionesButton } from "@/components/ViewInstruccionesButton";
import { Settings2, Pencil, Plus, Lock, Unlock, ListPlus, RotateCcw } from "lucide-react";

const ESTADOS_RUTA = {
  borrador:    { label: "📝 Borrador",  color: "bg-purple-500/20 text-purple-300" },
  planificada: { label: "Planificada", color: "bg-amber-500/20 text-amber-300" },
  en_curso:    { label: "En curso",    color: "bg-blue-500/20 text-blue-300" },
  finalizada:  { label: "Finalizada",  color: "bg-emerald-500/20 text-emerald-300" },
  cancelada:   { label: "Cancelada",   color: "bg-zinc-500/20 text-zinc-400" },
};

export default function RutaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [vehiculo, setVehiculo] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [cargaOpen, setCargaOpen] = useState(false);
  const [routexlOpen, setRoutexlOpen] = useState(false);
  const [planificarOpen, setPlanificarOpen] = useState(false);
  const [paradaPlanificar, setParadaPlanificar] = useState(null);
  const [editAsignacionOpen, setEditAsignacionOpen] = useState(false);
  const [addParadaExtraOpen, setAddParadaExtraOpen] = useState(false);
  // Iter 109 — edición de paradas protegida con PIN de supervisor
  const [editPin, setEditPin] = useState(null);
  const { hasPermission } = useAuth();
  const puedeResetear = hasPermission("rutas:resetear_parada");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [addLineasOpen, setAddLineasOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/rutas/${id}`);
    setR(data);
    if (data.vehiculo_id) {
      const v = await api.get("/vehiculos");
      setVehiculo(v.data.find((x) => x.id === data.vehiculo_id));
    }
    try {
      const { data: cl } = await api.get(`/rutas/${id}/checklist-carga`);
      setChecklist(cl);
    } catch (e) { /* ignore — no acceso */ }
  };
  useEffect(() => { load(); }, [id]);

  const cambiarEstado = async (e) => {
    await api.patch(`/rutas/${id}`, { estado: e });
    load();
  };

  const abrirPDF = () => {
    const token = localStorage.getItem("dnf_access_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rutas/${id}/parte.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => window.open(URL.createObjectURL(blob), "_blank"));
  };

  const borrar = async () => {
    if (!window.confirm("¿Eliminar esta ruta?")) return;
    await api.delete(`/rutas/${id}`);
    toast.success("Ruta eliminada");
    nav("/rutas");
  };

  const reabrirParada = async (orden, titulo, cierre) => {
    const motivo = window.prompt(`Reabrir la parada "${titulo}" (#${orden}) cerrada como ${cierre.toUpperCase()}.\n\nSe revierte el estado del montaje, de los componentes y de la línea. Las fotos se conservan.\n\nMotivo (opcional):`, "Reporte hecho en la parada equivocada");
    if (motivo === null) return;
    try {
      const { data } = await api.post(`/rutas/${id}/paradas/${orden}/reabrir`, { motivo }, { headers: { "X-Ruta-Edit-Pin": editPin } });
      toast.success(`Parada #${orden} reabierta (antes: ${data.cierre_previo})`);
      (data.avisos || []).forEach((a) => toast.warning(a, { duration: 9000 }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error reabriendo parada");
    }
  };

  const borrarParada = async (orden, titulo, cierre, parada) => {
    if (cierre === "no_realizada" && parada && !parada.decision_no_realizada) {
      toast.error("Esta parada está pendiente de decisión: Volver disponible / 2ª visita / Cerrar no procede. No se puede eliminar hasta decidir.",
        { duration: 9000, action: { label: "Ir a No realizadas", onClick: () => nav("/no-realizadas") } });
      return;
    }
    const msg = cierre
      ? `⚠️ La parada "${titulo}" (#${orden}) está cerrada como ${cierre.toUpperCase()}.\n\nSe revertirá el cierre (componentes, línea y petición) y se eliminará de la ruta. El trabajo volverá a estar disponible.\n\n¿Continuar?`
      : `¿Eliminar la parada "${titulo}" (#${orden})?\n\nLa parada volverá a estar disponible para otras rutas.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/rutas/${id}/paradas/${orden}${cierre ? "?force=true" : ""}`, { headers: { "X-Ruta-Edit-Pin": editPin } });
      toast.success(`Parada #${orden} eliminada · el trabajo vuelve a estar disponible`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "no_realizada_pendiente_decision") {
        toast.error(d.msg, { duration: 9000, action: { label: "Ir a No realizadas", onClick: () => nav("/no-realizadas") } });
        return;
      }
      toast.error(d?.msg || (typeof d === "string" ? d : null) || "Error eliminando parada");
    }
  };

  if (!r) return <div className="p-8 text-zinc-500">Cargando…</div>;

  if (cargaOpen) {
    return <CargaRuta rutaId={id} onBack={() => { setCargaOpen(false); load(); }} />;
  }

  const est = ESTADOS_RUTA[r.estado] || ESTADOS_RUTA.planificada;
  const cargaPct = checklist && checklist.total > 0 ? Math.round((checklist.cargados / checklist.total) * 100) : 0;

  return (
    <div className="p-6 max-w-6xl">
      <Link to="/rutas" className="text-xs text-zinc-500 hover:text-amber-300 inline-flex items-center mb-3">
        <ArrowLeft className="w-3 h-3 mr-1" /> Rutas
      </Link>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm text-zinc-500 font-mono">{r.codigo}</span>
            {r.correccion_pendiente && (
              <span data-testid="ruta-correccion-badge" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/40 font-bold"
                title="Hay paradas reseteadas: el montaje debe repetirse y volver a enrutarse">
                ♻️ Corrección pendiente
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${est.color}`}>{est.label}</span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-100">{r.nombre}</h1>
          <div className="text-sm text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
            <span>📅 {r.fecha}</span>
            {r.hora_salida && <span>🕗 Salida {r.hora_salida}</span>}
            {r.hora_regreso && <span>🕔 Regreso {r.hora_regreso}</span>}
            {r.provincia_principal && <span><MapPin className="w-3 h-3 inline" /> {r.provincia_principal}{r.provincia_secundaria ? ` / ${r.provincia_secundaria}` : ""}</span>}
            <DestinosResumen ruta={r} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Button onClick={abrirPDF} data-testid="ruta-pdf-btn"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            <Printer className="w-4 h-4 mr-1" /> Parte de trabajo PDF
          </Button>
          <Button onClick={() => setRoutexlOpen(true)} data-testid="ruta-routexl-btn"
            variant="outline" className="bg-transparent border-amber-700/40 text-amber-300 hover:bg-amber-500/10">
            <RouteIcon className="w-4 h-4 mr-1" /> Optimizar ruta
          </Button>
          {hasPermission("rutas:resetear_parada") && <RutaIntegridadDialog rutaId={id} />}
          <select value={r.estado} onChange={(e) => cambiarEstado(e.target.value)}
            data-testid="ruta-estado-select"
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 rounded px-2 py-1 text-xs">
            {Object.entries(ESTADOS_RUTA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <Button onClick={borrar} variant="ghost" size="sm" className="text-zinc-500 hover:text-red-400 h-7 text-xs">
            <Trash2 className="w-3 h-3 mr-1" /> Eliminar
          </Button>
        </div>
      </div>

      {/* Cabecera operativa */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <button onClick={() => setEditAsignacionOpen(true)} data-testid="edit-montadores-btn"
          className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 text-left hover:border-amber-500/40 hover:bg-zinc-900/60 transition-colors group">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
            <Users className="w-3 h-3" /> Montadores
            <Pencil className="w-2.5 h-2.5 ml-auto opacity-30 group-hover:opacity-100 text-amber-300" />
          </div>
          {(r.montadores || []).length === 0 && <div className="text-xs text-zinc-500 italic">Sin asignar · clic para editar</div>}
          {(r.montadores || []).map((m, i) => (
            <div key={i} className="text-sm text-zinc-200 flex items-center gap-1.5">
              <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${m.usuario_id ? "bg-amber-500/20 text-amber-300" : "bg-violet-500/20 text-violet-300"}`}>
                {m.usuario_id ? "DNF" : "Ext"}
              </span>
              {m.nombre_visible}
              <span className="text-[10px] text-zinc-500">· {m.rol}</span>
            </div>
          ))}
        </button>
        <button onClick={() => setEditAsignacionOpen(true)} data-testid="edit-vehiculo-btn"
          className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 text-left hover:border-amber-500/40 hover:bg-zinc-900/60 transition-colors group">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
            <Truck className="w-3 h-3" /> Vehículo
            <Pencil className="w-2.5 h-2.5 ml-auto opacity-30 group-hover:opacity-100 text-amber-300" />
          </div>
          {r.vehiculo_propio ? (
            <>
              <div className="text-sm text-amber-300">🚐 Vehículo propio</div>
              <div className="text-[11px] text-amber-200/70 mt-0.5">Autónomo · sin parte de vehículo</div>
            </>
          ) : vehiculo ? (
            <>
              <div className="text-sm text-zinc-200">{vehiculo.nombre} {vehiculo.matricula && <span className="text-zinc-500 text-xs">({vehiculo.matricula})</span>}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{vehiculo.tipo}</div>
            </>
          ) : <div className="text-xs text-zinc-500 italic">Sin asignar · clic para editar</div>}
        </button>
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Kilómetros</div>
          <div className="text-sm text-zinc-200">
            Salida: <span className="font-semibold">{r.km_inicial ?? "—"}</span>
            <span className="mx-2 text-zinc-600">·</span>
            Regreso: <span className="font-semibold">{r.km_final ?? "—"}</span>
          </div>
          {r.km_inicial && r.km_final && (
            <div className="text-xs text-amber-300 mt-0.5">Total: {(r.km_final - r.km_inicial).toFixed(0)} km</div>
          )}
        </div>
      </div>

      {/* 🗺️ Mapa de la ruta */}
      {(r.paradas || []).length > 0 && (
        <div className="mb-4" data-testid="ruta-detail-mapa">
          <RutaMapa rutaId={id} height={320} />
        </div>
      )}

      {/* Checklist de carga (QR scanning) */}
      {checklist && (
        <div data-testid="ruta-carga-widget"
          className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 mb-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold">Carga de furgo</span>
              {checklist.carga_completada_at && (
                <span className="text-[10px] uppercase tracking-wider bg-emerald-500/30 text-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Completada
                </span>
              )}
            </div>
            <Button onClick={() => setCargaOpen(true)} size="sm"
              data-testid="abrir-carga-admin-btn"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 h-7 text-xs">
              <ScanLine className="w-3 h-3 mr-1" /> Abrir checklist
            </Button>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex-1 h-2 bg-zinc-800 rounded overflow-hidden">
              <div className={`h-full ${cargaPct === 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${cargaPct}%` }} />
            </div>
            <span className="font-mono text-zinc-300">{checklist.cargados} / {checklist.total}</span>
            {checklist.pendientes > 0 && (
              <span className="text-amber-300">⏳ {checklist.pendientes} pendiente(s)</span>
            )}
          </div>
          {checklist.carga_completada_at && (
            <div className="text-[11px] text-emerald-300 mt-1.5">
              Cerrada el {new Date(checklist.carga_completada_at).toLocaleString("es-ES")} por {checklist.carga_completada_por}
            </div>
          )}
        </div>
      )}

      {r.observaciones && (
        <div className="bg-amber-500/5 border-l-4 border-amber-500/60 rounded p-3 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">Instrucciones generales</div>
          <div className="text-sm text-zinc-200 whitespace-pre-wrap">{r.observaciones}</div>
        </div>
      )}

      {/* Parte de vehículo (uso, km, daños, incidencias, repostaje) — solo si NO es vehículo propio */}
      {!r.vehiculo_propio && (
        <div className="mb-4">
          <ParteVehiculoBlock
            rutaId={r.id}
            vehiculoId={r.vehiculo_id}
            montadores={r.montadores || []}
            onChange={load}
          />
        </div>
      )}

      {/* Adjuntos visibles para montadores */}
      <div className="mb-4">
        <RutaAdjuntos
          rutaId={r.id}
          adjuntos={r.adjuntos || []}
          onChange={(updater) => setR((prev) => ({ ...prev, adjuntos: typeof updater === "function" ? updater(prev.adjuntos || []) : updater }))}
          canEdit
          paradas={(r.paradas || []).map((p) => ({
            linea_id: p.linea_id,
            orden: p.orden,
            espacio: p.espacio,
          }))}
        />
      </div>

      {/* Gastos de la ruta + reparto entre clientes */}
      <div className="mb-4 bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-200">Gastos de la ruta</h3>
        </div>
        <GastosBlock rutaId={r.id} />
      </div>

      {/* Paradas */}
      <div className="space-y-3" data-testid="paradas-detail">
        {/* Iter 114 — Banner de borrador + publicar */}
        {r.estado === "borrador" && (
          <div className="bg-purple-500/10 border border-purple-500/40 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
            data-testid="borrador-banner">
            <div className="text-sm text-purple-200">
              📝 <b>Esta ruta es un BORRADOR.</b> Los montadores no la ven ni han sido avisados todavía.
            </div>
            <Button onClick={async () => {
                try {
                  await api.patch(`/rutas/${id}`, { estado: "planificada" });
                  toast.success("🚀 Ruta publicada · montadores avisados (in-app, email y WhatsApp)");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Error publicando"); }
              }}
              data-testid="publicar-ruta-btn"
              className="bg-purple-500 hover:bg-purple-400 text-white font-semibold">
              🚀 Publicar ruta
            </Button>
          </div>
        )}

        {/* Iter 109 — Edición de paradas protegida con PIN (añadir/quitar no realizadas) */}
        {r.estado !== "finalizada" && r.estado !== "cancelada" && (
          <div className="flex justify-end items-center gap-2 flex-wrap">
            {!editPin ? (
              <Button onClick={() => setPinDialogOpen(true)} size="sm"
                data-testid="editar-paradas-pin-btn"
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 h-8 text-xs">
                <Lock className="w-3.5 h-3.5 mr-1" /> Editar paradas (PIN)
              </Button>
            ) : (
              <>
                <span className="text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-1 rounded flex items-center gap-1">
                  <Unlock className="w-3 h-3" /> Edición desbloqueada
                </span>
                <Button onClick={() => setAddLineasOpen(true)} size="sm"
                  data-testid="add-lineas-ruta-btn"
                  className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 h-8 text-xs">
                  <ListPlus className="w-3.5 h-3.5 mr-1" /> Añadir líneas de trabajo
                </Button>
                <Button onClick={() => setAddParadaExtraOpen(true)} size="sm"
                  data-testid="add-parada-extra-btn"
                  className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/40 h-8 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Añadir parada extra
                </Button>
                <Button onClick={() => setEditPin(null)} size="sm" variant="ghost"
                  data-testid="bloquear-edicion-btn"
                  className="text-zinc-500 hover:text-zinc-300 h-8 text-xs" title="Bloquear edición">
                  <Lock className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </div>
        )}
        {(r.paradas || []).map((p, idx) => {
          const dir = p.es_manual
            ? [p.manual_direccion, p.manual_ciudad, p.manual_provincia].filter(Boolean).join(", ")
            : [p.espacio?.direccion, p.espacio?.cp, p.espacio?.ciudad].filter(Boolean).join(", ");
          const maps = dir ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}` : null;
          const comps = (p.componentes || []).filter((c) => !c.parent_id);
          const puedeBorrar = r.estado !== "finalizada" && r.estado !== "cancelada" && !!editPin;
          const puedeReabrir = !!p.estado_cierre && r.estado !== "cancelada" && !!editPin;
          const cierreCfg = {
            montado: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
            semi_montado: "bg-amber-500/15 text-amber-300 border-amber-500/40",
            incidencia: "bg-red-500/15 text-red-300 border-red-500/40",
            cancelado: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
            no_realizada: "bg-red-900/40 text-red-200 border-red-700",
          }[p.estado_cierre];
          return (
            <React.Fragment key={idx}>
            <DestinoHeader destino={destinoDeParada(r.destinos, idx)} />
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="bg-zinc-950 px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="bg-amber-500 text-zinc-900 font-bold text-sm w-7 h-7 rounded-full flex items-center justify-center">{p.orden}</span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-100">
                      {p.es_manual ? (p.manual_titulo || "Parada manual") : (p.espacio?.nombre || "—")}
                      {p.es_manual && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded">extra</span>}
                    </div>
                    <div className="text-[11px] text-amber-400 font-mono">
                      {p.es_manual
                        ? (p.manual_cliente_nombre || (p.manual_importe ? `${p.manual_importe} €` : "—"))
                        : `${p.peticion?.codigo} · ${p.cliente_nombre}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.reseteada && (
                    <span data-testid={`parada-reseteada-${p.orden}`} title={`Reseteada por ${p.reseteada.por} · ${p.reseteada.at?.slice(0, 16).replace("T", " ")} · ${p.reseteada.motivo}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border font-bold bg-rose-500/15 text-rose-300 border-rose-500/40">
                      ♻️ Corrección · Reabierta
                    </span>
                  )}
                  {p.estado_cierre && !p.reseteada && puedeResetear && !p.es_manual && (
                    <ResetParadaDialog rutaId={id} orden={p.orden} titulo={p.espacio?.nombre} onDone={load} />
                  )}
                  {p.estado_cierre && (
                    <span data-testid={`parada-cierre-${p.orden}`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border font-bold ${cierreCfg}`}>
                      {p.estado_cierre.replace("_", " ")}{p.estado_cierre === "no_realizada" && p.no_realizada?.motivo ? ` · ${p.no_realizada.motivo}` : ""}{p.decision_no_realizada ? ` → ${p.decision_no_realizada.accion.replace(/_/g, " ")}` : ""}
                    </span>
                  )}
                  {p.estado_cierre === "no_realizada" && !p.decision_no_realizada && (
                    <Link to="/no-realizadas" data-testid={`parada-nr-pendiente-${p.orden}`}
                      className="text-[10px] px-2 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20">
                      ⏳ Pendiente de decisión: Volver disponible / 2ª visita / Cerrar no procede →
                    </Link>
                  )}
                  {p.hora_estimada && <span className="text-xs text-amber-300">🕒 {p.hora_estimada}</span>}
                  {puedeReabrir && (
                    <Button
                      size="sm" variant="ghost"
                      className="text-xs text-amber-300 hover:text-amber-100 h-7"
                      onClick={() => reabrirParada(p.orden, p.es_manual ? (p.manual_titulo || "extra") : (p.espacio?.nombre || "parada"), p.estado_cierre)}
                      data-testid={`reabrir-parada-${p.orden}`}
                      title="Revertir el cierre (montado/incidencia) de esta parada"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reabrir
                    </Button>
                  )}
                  {!p.es_manual && (
                    <Button
                      size="sm" variant="ghost"
                      className="text-xs text-cyan-400 hover:text-cyan-200 h-7"
                      onClick={() => { setParadaPlanificar({ ...p, _idx: idx }); setPlanificarOpen(true); }}
                      data-testid={`planificar-parada-${idx}`}
                      title="Planificar qué componentes van en esta parada"
                    >
                      <Settings2 className="w-3.5 h-3.5 mr-1" />
                      Planificar
                    </Button>
                  )}
                  {!p.es_manual && p.linea_id && (
                    <ViewInstruccionesButton
                      lineaId={p.linea_id}
                      size="xs"
                      variant="outline"
                      label="Instrucciones"
                      testId={`view-instr-parada-${idx}`}
                    />
                  )}
                  {!p.es_manual && p.peticion?.id && (
                    <Link to={`/peticiones/${p.peticion?.id}`} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                      Ver petición <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                  {puedeBorrar && (
                    <Button
                      size="sm" variant="ghost"
                      className="text-xs text-red-400 hover:text-red-200 h-7"
                      onClick={() => borrarParada(p.orden, p.es_manual ? (p.manual_titulo || "extra") : (p.espacio?.nombre || "parada"), p.estado_cierre, p)}
                      data-testid={`borrar-parada-${p.orden}`}
                      title={p.estado_cierre === "no_realizada" && !p.decision_no_realizada ? "Pendiente de decisión en No realizadas: no se puede eliminar" : p.estado_cierre ? "Eliminar esta parada (revierte antes su cierre)" : "Eliminar esta parada"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2">
                  <MapPin className="w-3 h-3" /> {dir || "Sin dirección"}
                  {maps && <a href={maps} target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 ml-1">Llévame ↗</a>}
                </div>
                {p.es_manual && p.manual_importe != null && (
                  <div className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5 text-[11px] text-emerald-200 mb-2">
                    💰 Importe a facturar: <b>{p.manual_importe} €</b>
                    {p.manual_concepto && <span className="text-emerald-300/80">· {p.manual_concepto}</span>}
                  </div>
                )}
                {p.observaciones && (
                  <div className="bg-amber-500/5 border-l-2 border-amber-500/60 rounded p-2 mb-2 text-xs text-zinc-200">
                    <b className="text-amber-300">Instrucciones:</b> {p.observaciones}
                  </div>
                )}
                {!p.es_manual && p.detalle_montaje && (
                  <div className="mt-2">
                    <DetalleMontaje detalle={p.detalle_montaje} campana={p.peticion?.codigo} marca={p.peticion?.marca} testId={`detalle-montaje-${p.orden}`} />
                  </div>
                )}
                <details className="mt-2 group">
                <summary className="text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300">Estado técnico de componentes ({comps.length})</summary>
                <ul className="text-xs text-zinc-300 mt-1 space-y-0.5">
                  {comps.map((c) => {
                    const codigo = String(c.id || "").slice(0, 6).toUpperCase();
                    return (
                      <li key={c.id} className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5">{codigo}</span>
                        <span>{c.nombre} · {c.cantidad}×</span>
                        {c.ancho_cm && c.alto_cm && <span className="text-zinc-500">{c.ancho_cm}×{c.alto_cm}cm</span>}
                        {c.bulto_numero && <span className="text-emerald-400 text-[10px]">📦 Bulto {c.bulto_numero}</span>}
                      </li>
                    );
                  })}
                  {comps.length === 0 && <li className="text-zinc-500 italic">Sin componentes</li>}
                </ul>
                </details>
              </div>
            </div>
            </React.Fragment>
          );
        })}
        {(r.paradas || []).length === 0 && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500">
            Esta ruta no tiene paradas asignadas.
          </div>
        )}
      </div>

      <RouteXLOptimizeDialog open={routexlOpen} onOpenChange={setRoutexlOpen}
        rutaId={id} paradas={r.paradas || []} onApplied={load} />

      <PlanificarParadaDialog
        open={planificarOpen}
        onOpenChange={setPlanificarOpen}
        rutaId={id}
        paradaIdx={paradaPlanificar?._idx}
        parada={paradaPlanificar}
        fechaRuta={r.fecha}
        onSaved={load}
      />

      <EditMontadoresVehiculoDialog
        open={editAsignacionOpen}
        onClose={() => setEditAsignacionOpen(false)}
        ruta={r}
        onSaved={load}
      />

      <AddParadaExtraDialog
        open={addParadaExtraOpen}
        onOpenChange={setAddParadaExtraOpen}
        rutaId={id}
        editPin={editPin}
        onAdded={load}
      />

      <RutaEditPinDialog
        open={pinDialogOpen}
        onOpenChange={setPinDialogOpen}
        onUnlocked={setEditPin}
      />

      <AddLineasRutaDialog
        open={addLineasOpen}
        onOpenChange={setAddLineasOpen}
        rutaId={id}
        editPin={editPin}
        lineasEnRuta={(r.paradas || []).map((p) => p.linea_id).filter(Boolean)}
        onAdded={load}
      />
    </div>
  );
}
